import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { FileService, FilesResponse } from '../../../services/file.service';
import { GoogleAuthService } from '../../../services/google-auth.service';
import { LoginRequiredDialogComponent } from '../login-required-dialog/login-required-dialog.component';

@Component({
  selector: 'app-download-button',
  standalone: true,
  imports: [CommonModule, MatDialogModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      type="button"
      class="dl"
      [class.dl--ghost]="ghost"
      [disabled]="downloading"
      (click)="onClick()"
      [attr.aria-label]="ariaLabel()">
      <span class="material-icons dl__icon" aria-hidden="true">{{ downloading ? 'hourglass_empty' : 'download' }}</span>
      <span class="dl__label">{{ label || defaultLabel() }}</span>
    </button>
  `,
  styles: [`
    .dl {
      display: inline-flex;
      align-items: center;
      gap: var(--space-2);
      padding: var(--space-2) var(--space-4);
      border-radius: var(--radius-md);
      border: 1px solid var(--color-border);
      background: var(--color-surface);
      color: var(--color-text-primary);
      font-size: var(--fs-sm);
      font-weight: var(--fw-medium);
      cursor: pointer;
      transition: background var(--dur-fast) var(--ease-out),
                  border-color var(--dur-fast) var(--ease-out),
                  color var(--dur-fast) var(--ease-out);
    }
    .dl:hover:not([disabled]) {
      border-color: var(--color-primary-500);
      color: var(--color-primary-600);
      background: var(--color-primary-50);
    }
    .dl[disabled] { opacity: 0.6; cursor: progress; }
    .dl--ghost { background: transparent; }
    .dl__icon { font-size: 18px; }
  `]
})
export class DownloadButtonComponent {
  @Input({ required: true }) bundle!: FilesResponse;
  @Input({ required: true }) fileType!: 'png' | 'pdf' | 'html' | 'txt' | 'docx' | string;
  @Input() lang: 'en' | 'hi' = 'en';
  @Input() label?: string;
  @Input() ghost = false;
  @Output() downloaded = new EventEmitter<{ bundleId: number; fileType: string }>();

  private fileService = inject(FileService);
  private auth = inject(GoogleAuthService);
  private dialog = inject(MatDialog);

  downloading = false;

  defaultLabel(): string {
    return this.fileType.toUpperCase();
  }

  ariaLabel(): string {
    return `Download ${this.fileType.toUpperCase()} for "${this.bundle.topic ?? 'this post'}"`;
  }

  onClick(): void {
    if (!this.auth.isLoggedIn()) {
      this.dialog.open(LoginRequiredDialogComponent, { width: '420px' });
      return;
    }
    const resolved = this.fileService.resolveFileKey(this.bundle, this.fileType, this.lang);
    if (!resolved) return;
    this.downloading = true;
    this.fileService.downloadFileByType(this.bundle.id, resolved).subscribe({
      next: blob => {
        const filename = this.bundle.files[resolved]?.filename ?? `${this.bundle.topic ?? 'file'}.${this.fileType}`;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 0);
        this.downloading = false;
        this.downloaded.emit({ bundleId: this.bundle.id, fileType: resolved });
      },
      error: () => { this.downloading = false; }
    });
  }
}
